/**
 * RfiForm — Request for Information form (AISC §1.6.0 / Form F-1.6).
 *
 * Redesigned 2026-05-05 (CR-2026-FE-RFI-FORM-REDESIGN, Class P) to be
 * "true to the paper form" (per D-033 §3.1, the Chesakl F-1.6 source-of-
 * design at sfm-drawing-feature/docs/qms/_source/forms-chesakl-templates/
 * F1.6.0DetailingRFIForm.doc) and visually polished. The previous version
 * was a bare 6-field MUI stack with no resemblance to the paper layout.
 *
 * Design moves (Apple-style):
 *   - Static section labels (uppercase tracking-wider, no floating labels)
 *   - 2-column "Issued To" / "Project Reference" metadata grid matching
 *     the Chesakl F-1.6 paper layout
 *   - Divider-titled "RESPONSE" section that is read-only for DRAFT and
 *     becomes editable post-SENT (lifecycle-aware)
 *   - Generous whitespace, subtle 1px borders, 16px border-radius card
 *   - Status pill in the header
 *   - Closing footer line preserves the Chesakl "Thank you for your
 *     prompt response..." courtesy text
 *   - ATTN field captured separately client-side and combined into
 *     requested_of_party on save (schema doesn't have a dedicated column;
 *     proper split is a future AP-class schema migration)
 *
 * Backend contract unchanged. createRfi / updateRfi shapes match the
 * existing /api/rfi endpoints.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Alert, Box, Chip, Divider,
  TextField, Breadcrumbs, Link as MuiLink, Grid, IconButton,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DescriptionOutlined,
} from "@mui/icons-material";

import { getRfi, createRfi, updateRfi } from "../../utils/rfisApi";

// ─────────────────────────────────────────────────────────────────────────
// Style helpers — keeping the Apple-clean look defined inline so the form
// is self-contained and easy to copy / re-use in other AISC paper-form
// surfaces (Transmittal, Print Issue Ticket, etc.) without dragging in a
// theme dependency.
// ─────────────────────────────────────────────────────────────────────────

const SECTION_LABEL = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 1,
};

const FIELD_LABEL = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 0.5,
};

const STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#495057" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  ANSWERED:  { bg: "#d1e7dd", color: "#0a3622" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#fde7e9", color: "#842029" },
};

// Heuristic — split a stored "Acme Steel · ATTN: Joe Smith" string back
// into its company + ATTN parts on edit, so existing rows round-trip.
function splitRequestedOf(combined) {
  if (!combined) return { company: "", attn: "" };
  const m = combined.match(/^(.*?)\s*[·•|]\s*ATTN:?\s*(.+)$/i);
  if (m) return { company: m[1].trim(), attn: m[2].trim() };
  // Comma-separated fallback: "Acme Steel, ATTN: Joe"
  const m2 = combined.match(/^(.*?),\s*ATTN:?\s*(.+)$/i);
  if (m2) return { company: m2[1].trim(), attn: m2[2].trim() };
  return { company: combined, attn: "" };
}

function combineRequestedOf(company, attn) {
  const c = (company || "").trim();
  const a = (attn || "").trim();
  if (c && a) return `${c} · ATTN: ${a}`;
  return c || a || "";
}

export default function RfiForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isCreate = !id || id === "new";
  const jobIdParam = params.get("job_id") || "";

  const [rfi, setRfi] = useState(null);
  const [jobId, setJobId] = useState(jobIdParam);
  const [referenceDrawingId, setReferenceDrawingId] = useState("");
  const [referenceDrawingText, setReferenceDrawingText] = useState("");
  const [requestedOfCompany, setRequestedOfCompany] = useState("");
  const [requestedOfAttn, setRequestedOfAttn] = useState("");
  const [requestedOfEmail, setRequestedOfEmail] = useState("");
  const [informationRequested, setInformationRequested] = useState("");
  const [suggestions, setSuggestions] = useState("");

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const refresh = () => {
    if (isCreate) return;
    setLoading(true);
    getRfi(id)
      .then((r) => {
        setRfi(r);
        setJobId(String(r.job_id || ""));
        setReferenceDrawingId(String(r.reference_drawing_id || ""));
        setReferenceDrawingText(r.reference_drawing_text || "");
        const split = splitRequestedOf(r.requested_of_party || "");
        setRequestedOfCompany(split.company);
        setRequestedOfAttn(split.attn);
        setRequestedOfEmail(r.requested_of_email || "");
        setInformationRequested(r.information_requested || "");
        setSuggestions(r.suggestions || "");
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id, isCreate]);

  const handleSave = async () => {
    setError(null);
    if (!jobId) {
      setError("Job ID is required.");
      return;
    }
    if (!requestedOfCompany.trim()) {
      setError("Requested Of (Company / Party) is required.");
      return;
    }
    if (!informationRequested.trim()) {
      setError("Information Requested cannot be blank — describe the question.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        job_id: Number(jobId),
        reference_drawing_id: referenceDrawingId ? Number(referenceDrawingId) : null,
        reference_drawing_text: referenceDrawingText || null,
        requested_of_party: combineRequestedOf(requestedOfCompany, requestedOfAttn),
        requested_of_email: requestedOfEmail || null,
        information_requested: informationRequested,
        suggestions: suggestions || null,
      };
      if (isCreate) {
        const created = await createRfi(payload);
        navigate(`/rfis/${created.id}`);
      } else {
        const { job_id: _ignore, ...editable } = payload;
        await updateRfi(id, editable);
        refresh();
      }
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.error || err?.message);
    }
  };

  const status = rfi?.status || "DRAFT";
  const canEdit = isCreate || status === "DRAFT";
  const statusStyle = STATUS_COLOR[status] || STATUS_COLOR.DRAFT;

  // Display values for the right metadata column
  const rfiNumber = rfi?.rfi_number || (isCreate ? "Assigned automatically" : `#${id}`);
  const rfiDate = useMemo(() => {
    if (rfi?.requested_at) return new Date(rfi.requested_at).toLocaleDateString();
    if (rfi?.created_at) return new Date(rfi.created_at).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [rfi]);

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="text.secondary">Loading RFI…</Typography>
      </Container>
    );
  }

  return (
    <>
      <Helmet><title>{isCreate ? "New RFI" : `RFI ${rfi?.rfi_number || ""}`} | SFM</title></Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", pt: 3, pb: 6 }}>
        <Container maxWidth="md">
          {/* Breadcrumbs + back nav */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" />
                  <span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/rfis" color="inherit" underline="hover">RFIs</MuiLink>
              <Typography color="text.primary">
                {isCreate ? "New" : (rfi?.rfi_number || `#${id}`)}
              </Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/rfis")} aria-label="Back to RFI list">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!isCreate && status !== "DRAFT" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This RFI is in <strong>{status}</strong> state — fields are read-only.
              Use the detail page to record an answer or close it.
            </Alert>
          )}

          {/* THE FORM CARD */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "background.paper",
            }}
          >
            {/* HEADER STRIP — title + form-id metadata + status pill */}
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "flex-start" }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <DescriptionOutlined fontSize="small" sx={{ color: "text.secondary" }} />
                  <Typography variant="overline" sx={{ letterSpacing: "0.12em", color: "text.secondary", fontWeight: 700 }}>
                    SFM · Form F-1.6
                  </Typography>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
                  Request for Information
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  AISC §1.6.0 detailing RFI
                </Typography>
              </Box>
              <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={1.5}>
                <Chip
                  label={status}
                  size="small"
                  sx={{
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                  }}
                />
                <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                    RFI No.
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 500 }}>
                    {rfiNumber}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                    Date
                  </Typography>
                  <Typography variant="body2">{rfiDate}</Typography>
                </Box>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            {/* TWO-COLUMN METADATA GRID — left "Requested Of" / right "Project Reference" */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Typography sx={SECTION_LABEL}>Requested Of</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography sx={FIELD_LABEL}>Company / Party *</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Super General Contractors"
                      value={requestedOfCompany}
                      onChange={(e) => setRequestedOfCompany(e.target.value)}
                      disabled={!canEdit}
                      InputLabelProps={{ shrink: false }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={FIELD_LABEL}>Attention</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Joe Owner — Owner's REP"
                      value={requestedOfAttn}
                      onChange={(e) => setRequestedOfAttn(e.target.value)}
                      disabled={!canEdit}
                    />
                  </Box>
                  <Box>
                    <Typography sx={FIELD_LABEL}>Email</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      placeholder="recipient@example.com"
                      value={requestedOfEmail}
                      onChange={(e) => setRequestedOfEmail(e.target.value)}
                      disabled={!canEdit}
                    />
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={SECTION_LABEL}>Project Reference</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography sx={FIELD_LABEL}>Job No. *</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="e.g., 24-101"
                      value={jobId}
                      onChange={(e) => setJobId(e.target.value)}
                      disabled={!isCreate}
                      helperText={isCreate ? null : "Job is locked once an RFI is created."}
                    />
                  </Box>
                  <Box>
                    <Typography sx={FIELD_LABEL}>Reference Drawing</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., S-200 & A-405"
                      value={referenceDrawingText}
                      onChange={(e) => setReferenceDrawingText(e.target.value)}
                      disabled={!canEdit}
                    />
                  </Box>
                  <Box>
                    <Typography sx={FIELD_LABEL}>Drawing ID (optional)</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Internal drawing ID, if linked"
                      value={referenceDrawingId}
                      onChange={(e) => setReferenceDrawingId(e.target.value)}
                      disabled={!canEdit}
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 4 }} />

            {/* QUESTION + SUGGESTION blocks */}
            <Box sx={{ mb: 4 }}>
              <Typography sx={SECTION_LABEL}>Information Requested *</Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Describe the conflict, ambiguity, or clarification needed.&#10;Include drawing references, dimensions, and any context the recipient needs to respond accurately.&#10;&#10;Example:  A conflict in elevation exists between drawing S-200 and A-405 at the Low Roof / Cafetorium. S-200 shows TOS elevation 117'-0&quot; and A-405 shows TOS elevation 119'-4-1/2&quot;."
                value={informationRequested}
                onChange={(e) => setInformationRequested(e.target.value)}
                disabled={!canEdit}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                Be specific. Recipient will copy this verbatim into their response.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography sx={SECTION_LABEL}>Clarification Sought <Typography component="span" variant="caption" sx={{ color: "text.disabled", textTransform: "none", letterSpacing: 0, fontWeight: 400, ml: 0.5 }}>· what response would resolve the question (optional)</Typography></Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder='e.g., "Please confirm which TOS elevation governs at this location." or "Please specify the bolt grade required for this connection per the contract documents."'
                value={suggestions}
                onChange={(e) => setSuggestions(e.target.value)}
                disabled={!canEdit}
              />
            </Box>

            {/* RESPONSE SECTION — read-only on DRAFT, editable when SENT */}
            {!isCreate && (
              <>
                <Divider sx={{ my: 4 }}>
                  <Typography sx={{ ...SECTION_LABEL, mb: 0, color: "text.disabled" }}>Response</Typography>
                </Divider>
                <Box sx={{ mb: 4 }}>
                  <Typography sx={FIELD_LABEL}>Answer</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    placeholder={status === "DRAFT" ? "Recipient's answer will appear here once provided." : "Recorded answer."}
                    value={rfi?.answer_text || ""}
                    disabled
                  />
                </Box>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={8}>
                    <Typography sx={FIELD_LABEL}>Answered By</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={rfi?.answered_by_party || ""}
                      placeholder="—"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={FIELD_LABEL}>Response Date</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={rfi?.answered_at ? new Date(rfi.answered_at).toLocaleDateString() : ""}
                      placeholder="—"
                      disabled
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {/* Closing courtesy line — preserved from Chesakl source per D-033 §3.1 */}
            <Box sx={{ mt: 5, pt: 3, borderTop: "1px solid", borderColor: "divider", textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
                Thank you for your prompt response to this request for information.
              </Typography>
            </Box>
          </Paper>

          {/* ACTION BAR */}
          {canEdit && (
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
              <Button
                variant="text"
                onClick={() => navigate("/rfis")}
                disabled={saving}
                sx={{ color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                }}
              >
                {saving ? "Saving…" : (isCreate ? "Create RFI" : "Save changes")}
              </Button>
            </Stack>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              per AISC §1.6.0 — request for information
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
}
